import { Dialog, DialogPanel } from "@tremor/react";
import { AlertTriangle, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useDeleteGroup } from "../queries/groups.js";

export default function DeleteGroupDialog({ group, onClose, onDeleted }) {
  const [alsoDeleteMonitors, setAlsoDeleteMonitors] = useState(false);
  const mutation = useDeleteGroup();
  const deleting = mutation.isPending;

  const monitorCount = group.member_count ?? group.members?.length ?? 0;

  async function handleConfirm() {
    try {
      const result = await mutation.mutateAsync({
        id: group.id,
        deleteMonitors: alsoDeleteMonitors,
      });
      if (alsoDeleteMonitors && result?.deleted_monitor_count > 0) {
        toast.success(
          `Group deleted along with ${result.deleted_monitor_count} monitor${
            result.deleted_monitor_count === 1 ? "" : "s"
          }`,
        );
      } else {
        toast.success("Group deleted");
      }
      onDeleted?.(group.id);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error ?? "Failed to delete group");
    }
  }

  return (
    <Dialog open onClose={deleting ? () => {} : onClose} static>
      <DialogPanel className="max-w-md rounded-xl bg-white p-0 shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Delete group?</h2>
              <p className="text-xs text-gray-500">This action cannot be undone.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <p className="text-sm text-gray-700">
            You're about to delete{" "}
            <span className="font-semibold text-gray-900">"{group.name}"</span>.
            {monitorCount > 0 ? (
              <>
                {" "}It contains{" "}
                <span className="font-semibold">
                  {monitorCount} monitor{monitorCount === 1 ? "" : "s"}
                </span>
                .
              </>
            ) : (
              " It has no monitors."
            )}
          </p>

          {monitorCount > 0 && (
            <label
              className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm transition ${
                alsoDeleteMonitors
                  ? "border-red-300 bg-red-50"
                  : "border-gray-200 bg-gray-50 hover:bg-gray-100"
              }`}
            >
              <input
                type="checkbox"
                checked={alsoDeleteMonitors}
                onChange={(e) => setAlsoDeleteMonitors(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="flex-1">
                <span className="font-medium text-gray-900">
                  Also delete all {monitorCount} monitor
                  {monitorCount === 1 ? "" : "s"} in this group
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  This permanently removes the monitors and all their check
                  history. Otherwise the monitors stay and just lose their
                  group membership.
                </span>
              </span>
            </label>
          )}

          {alsoDeleteMonitors && monitorCount > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <strong>Warning:</strong> {monitorCount} monitor
              {monitorCount === 1 ? "" : "s"} and all associated check results,
              incidents, alerts, and uptime history will be permanently deleted.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={deleting}
            className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
          >
            {deleting
              ? "Deleting…"
              : alsoDeleteMonitors && monitorCount > 0
                ? `Delete group + ${monitorCount} monitor${monitorCount === 1 ? "" : "s"}`
                : "Delete group"}
          </button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
